import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { T } from '@flipster/i18n';
import { Tooltip } from '@flipster/react/design-system/tooltip';
import { css } from '@flipster/styled-system/css';
import { Price } from '@flipster/ui';
import { countDecimalPlaces } from '@flipster/utils/num';

import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import { marginTypeBunja } from './bunja/base';
import { formBunja } from './bunja/form';
import { liquidationPreviewBunja } from './bunja/liquidation';
import { col, info, row } from './styles';

const LiquidationInfo = () => {
  const transPosition = useTranslations('Position.r250401');
  const { symbolInfoAtom } = useBunja(tradeSymbolBunja);
  const symbolInfo = useAtomValue(symbolInfoAtom);
  const { throttledLiquidationPriceAtom } = useBunja(liquidationPreviewBunja);
  const liquidationPrice = useAtomValue(throttledLiquidationPriceAtom);
  const { marginTypeAtom } = useBunja(marginTypeBunja);
  const marginType = useAtomValue(marginTypeAtom);
  const { reduceOnlyAtom } = useBunja(formBunja);
  const reduceOnly = useAtomValue(reduceOnlyAtom);

  const tickSize = symbolInfo?.tickSize;
  const quoteCurrency = symbolInfo?.quoteCurrency;
  const precision = countDecimalPlaces(tickSize); // = pricePrecision;
  const isCross = marginType === 'Cross';

  const label = isCross ? (
    <Tooltip.Root placement='bottom-start'>
      <Tooltip.Trigger asChild>
        <p
          className={css({
            textStyle: 'Text/M/Strong',
            mb: 4,
            color: 'content.default.level-3',
            display: 'inline-block',
            borderBottomWidth: 1,
            borderBottomStyle: 'dashed',
            borderBottomColor: 'border.default.level-3',
          })}
        >
          <T id='Position.r250401.Est_liquidation_price' />
        </p>
      </Tooltip.Trigger>
      <Tooltip.Content bottomSheetTitle={<T id='Position.r250401.Est_liquidation_price' />}>
        <T id='Trade.r260506.Estimated_liquidation_price_desc' />
      </Tooltip.Content>
    </Tooltip.Root>
  ) : (
    <p className={css({ textStyle: 'Text/M/Strong', mb: 4, color: 'content.default.level-3' })}>
      {transPosition('Est_liquidation_price')}
    </p>
  );

  const placeholder = (
    <span className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' })}>–</span>
  );

  return (
    <div className={info}>
      {label}
      <div className={row}>
        <div className={col}>
          {reduceOnly ? (
            placeholder
          ) : (
            <Price
              fontType='num3'
              price={liquidationPrice.long ?? 0}
              currency={quoteCurrency}
              precision={precision}
              toFixed
              zeroString='-'
              className={css({
                '&> span:first-child': {
                  color: 'inverse.white',
                },
              })}
            />
          )}
        </div>
        <div className={col}>
          {reduceOnly ? (
            placeholder
          ) : (
            <Price
              fontType='num3'
              price={liquidationPrice.short ?? 0}
              currency={quoteCurrency}
              precision={precision}
              toFixed
              zeroString='-'
              className={css({
                '&> span:first-child': {
                  color: 'inverse.white',
                },
              })}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LiquidationInfo;
